import {
  IAccountManager,
  BloomerSourceType,
  BloomieTamSeniority,
  DealType,
  IBasics,
  IBloomies,
  IJobFunnelProject,
  ITalent,
  ITalentInput,
  JobType,
  LostReasonType,
  ProfileNetwork,
  PriorityType,
  Staff,
  StatusType,
} from '@bloomays-lib/types.shared';
import type { Bloomies, JobFunnelProject } from '@prisma-clients/api-talents';
import { getLKSlug } from './url.utils';
import { parseStringToDate, isDate } from './date.utils';

export const buildIBloomies = (accountManager: IAccountManager): IBloomies => {
  return {
    firstname: accountManager.firstname,
    lastname: accountManager.lastname,
    email: accountManager.email,
    externalId: accountManager.externalId,
    role: Staff.tam,
    hubspotUserId: accountManager.hubspotUserId,
    hubspotLabelNameId: accountManager.hubspotLabelNameId,
    slackId: accountManager.slackId,
    seniority: accountManager.seniority,
    isActive: accountManager.isActive,
  };
};

export const parsePrismaRoleToStaff = (role: string | null | undefined): Staff => {
  switch (role) {
    case 'csm':
      return Staff.csm;
    case 'tech':
      return Staff.system;
    case 'sales':
      return Staff.sales;
    default:
      return Staff.tam;
  }
};
export const parsePrismaBloomieToIBloomie = (bloomie: Bloomies): IBloomies => ({
  firstname: bloomie.firstname,
  lastname: bloomie.lastname,
  email: bloomie.email,
  externalId: bloomie.pipefyId || '',
  role: parsePrismaRoleToStaff(bloomie.role),
  hubspotUserId: bloomie.hubspotUserId || undefined,
  hubspotLabelNameId: bloomie.hubspotLabelNameId || undefined,
  phoneNumber: bloomie.phoneNumber || undefined,
  avatarUrl: bloomie.avatarUrl || undefined,
  slackId: bloomie.slackId || undefined,
  luccaId: bloomie.luccaId || undefined,
  airtableId: bloomie.airtableId || undefined,
  seniority: bloomie.seniority != null ? (bloomie.seniority as BloomieTamSeniority) : null,
  isActive: bloomie.isActive,
  staffEntryDate: bloomie.staffEntryDate ?? null,
});

export const removeGqlTypename = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(removeGqlTypename);
  } else if (obj !== null && typeof obj === 'object') {
    const newObj: any = {};
    for (const key in obj) {
      if (key !== '__typename') {
        newObj[key] = removeGqlTypename(obj[key]);
      }
    }
    return newObj;
  }
  return obj;
};

// talent to basics
export const talentToTalentBasics = (talent: ITalent): IBasics => {
  const basics: IBasics = {
    firstname: talent.firstname,
    lastname: talent.lastname,
    email: talent.email,
    phone: talent.phone,
    location: talent.location,
    url: talent.url ? getLKSlug(talent.url) : undefined,
    profiles: talent.profiles,
    image: talent.image,
    jobTitle: talent.positionTitle?.jobTitle.label || talent.jobTitle,
    positionTitle: talent.positionTitle,
    label: talent.label,
    tjm: talent.tjm,
    desiredTjm: talent.desiredTjm,
    package: talent.package,
    desiredPackage: talent.desiredPackage,
    remoteWork: talent.remoteWork,
    status: talent.status,
    origin: talent.origin,
    secondaryUrls: talent.secondaryUrls,
    hiresweetId: talent.hiresweetId,
    owner: talent.owner,
    bloomerStatus: talent.bloomerStatus,
    airtableId: talent.airtableId,
  };

  if (talent.url) {
    basics.profiles = [{ network: ProfileNetwork.LinkedIn, url: getLKSlug(talent.url) }];
  }

  return basics;
};

// ITalent to TalentV2Input
export const talentToTalentInput = (talent: ITalent): ITalentInput => {
  const work =
    talent.jobs?.map((job) => {
      return {
        id: job?.id,
        name: job.name,
        label: job.label,
        skills: job.skills,
        url: job.url,
        summary: job.description,
        position: [job?.jobTitle?.[0].label as string],
        startDate: isDate(job?.period?.startDate)
          ? parseStringToDate(job?.period?.startDate as string, 'yyyy-MM-dd')
          : undefined,
        endDate: isDate(job?.period?.endDate)
          ? parseStringToDate(job?.period?.endDate as string, 'yyyy-MM-dd')
          : undefined,
      };
    }) || [];
  const education =
    talent.educations?.map((education) => {
      return {
        id: education?.id,
        institution: education.name,
        url: education.url,
        area: education.label || 'Unknown',
        beginDate:
          education.period?.startDate === '' || education.period?.startDate === undefined
            ? undefined
            : new Date(education.period?.startDate),
        endDate:
          education.period?.endDate === '' || education.period?.endDate === undefined
            ? undefined
            : new Date(education.period?.endDate),
      };
    }) || [];

  const languages = talent.languages?.map((lang) => {
    return {
      id: lang?.id,
      label: lang.label,
      level: lang.level,
    };
  });
  return {
    id: talent?.id,
    basics: talentToTalentBasics(talent),
    work: work,
    education: education,
    skills: talent.skills?.map((skill) => {
      return {
        id: skill?.id,
        level: skill.level,
        iso: skill.skill.iso,
        alternateLabels: [],
        label: skill.skill.label,
      };
    }),
    languages: languages,
  };
};

export const parsePrismaJobFunnelToIJobFunnel = (
  dbJobFunnel: JobFunnelProject,
  jobOwnerBloomie: IBloomies | null,
  recruiterBloomie: IBloomies | null,
): IJobFunnelProject => {
  const job: IJobFunnelProject = {
    id: dbJobFunnel.id,
    name: dbJobFunnel.label,
    society: {
      name: dbJobFunnel.society,
      siret: '',
    },
    recruiter: {
      email: dbJobFunnel.recruiter,
      role: Staff.tam,
      firstname: '',
      lastname: '',
      externalId: '',
      isActive: false,
    },
    jobOwner: {
      email: dbJobFunnel.jobOwner,
      role: Staff.tam,
      firstname: '',
      lastname: '',
      externalId: '',
      isActive: false,
    },
    airtableId: dbJobFunnel.airtableId ?? undefined,
    pipefyId: dbJobFunnel.pipefyId ?? undefined,
    hiresweetId: dbJobFunnel.hiresweetId ?? undefined,
    jobType: dbJobFunnel.jobType as JobType,
    dealType: dbJobFunnel.dealType as DealType,
    jobTitle: dbJobFunnel.jobTitle,
    interlocuteur1Email: dbJobFunnel.interlocuteur1Email,
    interlocuteur2Email: dbJobFunnel.interlocuteur2Email,
    onSiteDaysPerWeek: dbJobFunnel.onSiteDaysPerWeek,
    location: dbJobFunnel.location,
    duration: dbJobFunnel.duration,
    remoteDaysPerWeek: dbJobFunnel.remoteDaysPerWeek,
    expectedStartDate: dbJobFunnel.expectedStartDate,
    activitySector: dbJobFunnel.activitySector,
    context: dbJobFunnel.context,
    details: dbJobFunnel.details,
    team: dbJobFunnel.team,
    minYearsXP: dbJobFunnel.minYearsXP,
    scoreCard1: dbJobFunnel.scoreCard1,
    scoreCard2: dbJobFunnel.scoreCard2,
    scoreCard3: dbJobFunnel.scoreCard3,
    scoreCard4: dbJobFunnel.scoreCard4,
    requiredLanguages: dbJobFunnel.requiredLanguages,
    confidentialNotes: dbJobFunnel.confidentialNotes,
    maxClientBudget: dbJobFunnel.maxClientBudget,
    clientBudgetDetails: dbJobFunnel.clientBudgetDetails,
    maxTalentBudget: dbJobFunnel.maxTalentBudget,
    outOfBudgetProfil: dbJobFunnel.outOfBudgetProfil,
    budgetDecisionMaker: dbJobFunnel.budgetDecisionMaker,
    isInternalValidation: dbJobFunnel.isInternalValidation,
    clientResearchTrigger: dbJobFunnel.clientResearchTrigger,
    interviewProcess: dbJobFunnel.interviewProcess,
    finalValidationResponsable: dbJobFunnel.finalValidationResponsable,
    minTotalProfilesToInclude: dbJobFunnel.minTotalProfilesToInclude,
    searchStatus: dbJobFunnel.searchStatus,
    competitors: dbJobFunnel.competitors,
    isExclusivity: dbJobFunnel.isExclusivity,
    cvDebriefDate: dbJobFunnel.cvDebriefDate,
    isResponsiveClient: dbJobFunnel.isResponsiveClient,
    isReliableClient: dbJobFunnel.isReliableClient,
    isDirectLinkWithDecisionMaker: dbJobFunnel.isDirectLinkWithDecisionMaker,
    priority: dbJobFunnel.priority as PriorityType | null,
    isBloomerReplacement: dbJobFunnel.isBloomerReplacement,
    isQuickContract: dbJobFunnel.isQuickContract,
    status: dbJobFunnel.status as StatusType,
    winDate: dbJobFunnel.winDate,
    lostReason: dbJobFunnel.lostReason as LostReasonType,
    otherLostReason: dbJobFunnel.otherLostReason ?? undefined,
    bloomerSource: dbJobFunnel.bloomerSource as BloomerSourceType,
    passeur: dbJobFunnel.passeur ?? undefined,
    buteur: dbJobFunnel.buteur ?? undefined,
    capitaine: dbJobFunnel.capitaine ?? undefined,
    defenseur: dbJobFunnel.defenseur ?? undefined,
    createdAt: dbJobFunnel.createdAt,
  };

  if (jobOwnerBloomie) {
    job.jobOwner = jobOwnerBloomie;
  }
  if (recruiterBloomie) {
    job.recruiter = recruiterBloomie;
  }

  return job;
};
